/**
 * DocumentFrame — the ONE way ANY Solum app previews a rendered document.
 *
 * ── it moved here from `apps/console/src/app` and that is the point (#23) ──
 * It was console-local while the console was the only app that rendered a
 * document. The customer portal now shows a customer their own sealed invoice
 * (#23 I6), and the choice was to copy this file or to share it. Copying a
 * SECURITY control is rule 18's shape at its most expensive: two `sandbox`
 * attributes, one commit away from disagreeing, and the one that is wrong is the
 * one nobody is looking at. So there is one component, in `@solum/ui`, with the
 * console's own reasoning carried across verbatim below.
 *
 * A document's HTML is DATA, and it is not escaped on the way in.
 * `documents.render_scalars` substitutes context values straight into the
 * template body and escapes only `regexp_replace`'s own replacement
 * metacharacters (documents `0001`, and `0008` deliberately removed even the
 * `&` half). So a quote line description, an account's display name or an
 * invoice line typed by whoever can edit the subject arrives in the output as
 * live markup.
 *
 * Previewed in a `srcDoc` iframe with no `sandbox`, that markup ran in the
 * console's OWN origin: `window.parent` and `localStorage` are both in reach,
 * and `@solum/auth` keeps the access AND refresh token in localStorage
 * (SEC-17 / #374). A line description was a session takeover.
 *
 * `sandbox=""` is deliberately EMPTY. It grants nothing, so the frame gets an
 * opaque origin with scripting disabled — the markup renders, and it cannot
 * execute. Two tokens must never be added here:
 *
 *   · `allow-same-origin` alone puts the frame back in the console's origin.
 *   · `allow-scripts` + `allow-same-origin` together are equivalent to no
 *     sandbox at all — a frame in its parent's origin can reach up and remove
 *     its own sandbox attribute.
 *
 * Nothing is lost by this. Every shipped document template (the `documents`,
 * `documents-cpq` and `documents-billing` migrations) is static markup — table,
 * footer, inline CSS at most; there is no `<script>` and no event handler in
 * any of them, and the previews render identically sandboxed. If a template
 * ever genuinely needs script, it does not get it by relaxing this attribute:
 * it gets served from a separate origin.
 *
 * The document is still downloadable (`Download .html`) — a file the operator
 * chose to open in their own tab is a different trust decision from markup this
 * app renders inline, unasked, on a page that holds a live session.
 *
 * The CSP half travels with it: `frame-src 'self'` was a console-only directive
 * in `@solum/csp` and is now emitted for both apps, because both now have the
 * surface it exists for. `apps/portal/e2e-browser/csp.spec.ts` proves the
 * preview renders under the portal's real policy rather than trusting the spec.
 */
export function DocumentFrame({ html, title, className, testId = 'doc-frame' }: {
  html: string
  title: string
  className?: string
  testId?: string
}) {
  return (
    <iframe
      className={className}
      title={title}
      sandbox=""
      srcDoc={html}
      data-testid={testId}
    />
  )
}
